import { defineStore } from "pinia";
import type { TableColumnTypeCustom } from "@/types/claims";
import { computed, markRaw, type Raw, ref } from "vue";
import {
  initDefaultColumns,
  useTableColumns,
} from "@/composables/table/tableColumns";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import axios from "axios";
import { createNotification } from "@/tools/notification";
import { MarketPlace } from "@/types/market-place";
import {
  CopyOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from "@ant-design/icons-vue";
import BarcodeIcon from "@/svgIcons/BarcodeIcon.vue";
import GroupAddIcon from "@/svgIcons/GroupAddIcon.vue";
import NoDocumentIcon from "@/svgIcons/NoDocumentIcon.vue";

export enum MarkingCodesRegistryStatus {
  Issued,
  Applied,
  Circulating,
  Withdrawn,
  WrittenOff,
}

export const MarkingCodesRegistryStatusTranslates: Record<
  MarkingCodesRegistryStatus,
  string
> = {
  [MarkingCodesRegistryStatus.Issued]: "Выпущен",
  [MarkingCodesRegistryStatus.Applied]: "Нанесён",
  [MarkingCodesRegistryStatus.Circulating]: "В обороте",
  [MarkingCodesRegistryStatus.Withdrawn]: "Выведен",
  [MarkingCodesRegistryStatus.WrittenOff]: "Списан",
};

export const MarkingCodesRegistryStatusOptions = Object.entries(
  MarkingCodesRegistryStatusTranslates,
).map(([key, title]) => ({
  value:
    MarkingCodesRegistryStatus[key as keyof typeof MarkingCodesRegistryStatus],
  title,
}));

export enum MarkingCodesRegistryMovement {
  SellerWarehouse,
  MarketplaceWarehouse,
  TransferredToBuyer,
  ReturningToSeller,
}

export const MarkingCodesRegistryMovementTranslates: Record<
  MarkingCodesRegistryMovement,
  string
> = {
  [MarkingCodesRegistryMovement.SellerWarehouse]: "На складе продавца",
  [MarkingCodesRegistryMovement.MarketplaceWarehouse]: "На складе МП",
  [MarkingCodesRegistryMovement.TransferredToBuyer]: "Передан покупателю",
  [MarkingCodesRegistryMovement.ReturningToSeller]: "Возвращается продавцу",
};

export const MarkingCodesRegistryMovementOptions = Object.entries(
  MarkingCodesRegistryMovementTranslates,
).map(([key, title]) => ({
  value:
    MarkingCodesRegistryMovement[
      key as keyof typeof MarkingCodesRegistryMovement
    ],
  title,
}));

export interface MarkingCodesRegistryTableItem {
  id: string;
  status: string;
  movement: string;
  marketplace: MarketPlace;
  code: string;
  barcode: string;
  productName: string;
  sellerArticle: string;
  gtin: string;
  supply: string;
  order: string;
  document: string;
  codeAuthor: string;
  createdDateCode: string;
  actions: null;
}

const INITIAL_FILTERS = {};

export const useMarkingCodesRegistry = defineStore(
  "markingCodesRegistryStore",
  () => {
    const filters = ref({ ...INITIAL_FILTERS });
    const getRegistryDataStatusLoad = ref<enumStatusLoad>(
      enumStatusLoad.NOT_STARTED,
    );

    const selectedRowKeys = ref<MarkingCodesRegistryTableItem["id"][]>([]);

    const isDataLoading = computed(
      () => getRegistryDataStatusLoad.value === enumStatusLoad.LOADING,
    );

    const pagination = ref({
      current: 1,
      total: 500,
      pageSize: 10,
      defaultPageSize: 10,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      showTotal: (total: number, range: [number, number]) =>
        `${range[0]}-${range[1]} из ${total}`,
      onChangePagination: async () => {
        await loadData();
      },
    });
    const tableData = ref<MarkingCodesRegistryTableItem[]>([]);
    const columnsConfigName = "baskitMarkingCodesRegistryTable";

    const markingCodesRegistryColumns = ref<
      TableColumnTypeCustom<MarkingCodesRegistryTableItem>[]
    >(
      initDefaultColumns<MarkingCodesRegistryTableItem>([
        { dataIndex: "id", title: "ID", fixed: "left" },
        { dataIndex: "code", title: "Код ЧЗ", fixed: "left" },
        { dataIndex: "marketplace", title: "МП заказа", fixed: "left" },
        { dataIndex: "status", title: "Статус" },
        { dataIndex: "movement", title: "Движение товара" },
        { dataIndex: "barcode", title: "Баркод" },
        { dataIndex: "productName", title: "Наименование товара" },
        { dataIndex: "sellerArticle", title: "Артикул продавца" },
        { dataIndex: "gtin", title: "GTIN" },
        { dataIndex: "supply", title: "Поставка" },
        { dataIndex: "order", title: "Заказ" },
        { dataIndex: "document", title: "Документ" },
        { dataIndex: "codeAuthor", title: "Автор кода" },
        { dataIndex: "createdDateCode", title: "Дата создания кода" },
        { dataIndex: "actions", title: "Действия", fixed: "right" },
      ]),
    );

    const { columnsFiltered, dragList, initDragList } = useTableColumns(
      markingCodesRegistryColumns,
      columnsConfigName,
      tableData,
    );

    initDragList();

    const createTableData = (data: any) => {
      const result: MarkingCodesRegistryTableItem[] = [
        {
          marketplace: MarketPlace.Wildberries,
          code: "code",
          codeAuthor: "codeAuthor",
          movement:
            MarkingCodesRegistryMovementTranslates[
              MarkingCodesRegistryMovement.MarketplaceWarehouse
            ],
          createdDateCode: "createdDateCode",
          status:
            MarkingCodesRegistryStatusTranslates[
              MarkingCodesRegistryStatus.Applied
            ],
          barcode: "barcode",
          gtin: "gtin",
          document: "document",
          order: "order",
          id: "id",
          productName: "productName",
          sellerArticle: "sellerArticle",
          actions: null,
          supply: "supply",
        },
      ];

      return result;
    };

    const loadData = async (tableFilters = filters.value) => {
      try {
        getRegistryDataStatusLoad.value = enumStatusLoad.LOADING;

        const take = pagination.value.pageSize;
        const skip = (pagination.value.current - 1) * pagination.value.pageSize;

        const response = await new Promise((resolve) =>
          setTimeout(() => resolve({ data: { item1: [], item2: 1000 } }), 2000),
        );

        tableData.value = createTableData(response.data.item1);
        pagination.value.total = response.data.item2;
        getRegistryDataStatusLoad.value = enumStatusLoad.LOADED;
        updateSelectedItems([]);
      } catch (error) {
        onError(error);
        getRegistryDataStatusLoad.value = enumStatusLoad.ERROR;
      }
    };

    const onError = (err: unknown) => {
      if (axios.isAxiosError(err)) {
        createNotification(
          "error",
          err.response?.data?.detail,
          undefined,
          "topRight",
        );
      }
    };

    const updateSelectedItems = (
      selectedKeys: MarkingCodesRegistryTableItem["id"][],
    ) => {
      selectedRowKeys.value = [...selectedKeys];
    };

    const actions: {
      tooltip: string;
      icon: Raw<any>;
      handler: (item: any) => void;
    }[] = [
      {
        tooltip: "PrinterOutlined",
        icon: markRaw(PrinterOutlined),
        handler: (item) => {},
      },
      {
        tooltip: "ReloadOutlined",
        icon: markRaw(ReloadOutlined),
        handler: (item) => {},
      },
      {
        tooltip: "CopyOutlined",
        icon: markRaw(CopyOutlined),
        handler: (item) => {},
      },
      {
        tooltip: "BarcodeIcon",
        icon: markRaw(BarcodeIcon),
        handler: (item) => {},
      },
      {
        tooltip: "GroupAddIcon",
        icon: markRaw(GroupAddIcon),
        handler: (item) => {},
      },
      {
        tooltip: "NoDocumentIcon",
        icon: markRaw(NoDocumentIcon),
        handler: (item) => {},
      },
    ];

    return {
      columnsFiltered,
      dragList,
      pagination,
      updateSelectedItems,
      getRegistryDataStatusLoad,
      selectedRowKeys,
      isDataLoading,
      columnsConfigName,
      loadData,
      tableData,
      actions,
    };
  },
);
